
import axios from 'axios';
import { PROVIDER_READ_ROLES } from './constants';
import {
  configureServiceStatus,
  isServiceFailure,
  isTransportFailure,
  reportServiceFailure,
} from './serviceStatus';
import { fetchApi, retriesExhaustedFor } from './requestRetry';
import { readResponseBody, responseError } from './apiResponse';
const BASE_URL = import.meta.env.VITE_BACKEND_URL || '/api';

// The service-down page: every request below reports transport and gateway
// failures here; the store confirms against /healthcheck before showing the
// page, and reloads the app once the outage is no longer proven. Never
// /healthz: Cloud Run's front end answers public paths ending in "z" with its
// own 404, so that check could never pass (11 Oct 2026).
configureServiceStatus({
  healthUrl: `${BASE_URL}/healthcheck`,
  onRecovered: () => window.location.reload(),
});

// Every call goes through requestRetry's bounded retry (Cloud Run's "no
// available instance" 429, an upstream 500/503 the app did not send, a short
// limiter Retry-After) unless `{ retry: false }` — uploads are never re-sent.
// Transport and gateway failures still report to the service-down store, but
// only the final answer does, so a blip that a retry absorbs never starts its
// confirmation streak. A call that recovered from an upstream 5xx fires
// `qc:edge-retry` (App.js refetches the queries that failed meanwhile).
const apiFetch = (url, init, options) => fetchApi(url, init, options);

// Errors from a call the retry layer gave up on carry `retriesExhausted`, so
// React Query hooks with their own retry count leave them alone (queryRetry.js).
const markRetriesExhausted = (error, response) => {
  if (retriesExhaustedFor(response) && error && typeof error === 'object') error.retriesExhausted = true;
  return error;
};
export const setCurrentRouteBeforeLogin = (route) => localStorage.setItem('route_before_login', route);
export const getCurrentRouteBeforeLogin = () => localStorage.getItem('route_before_login');
export const removeCurrentRouteBeforeLogin = () => localStorage.removeItem('route_before_login');
export const getLocalStorageToken = () => localStorage.getItem('token');
export const removeLocalStorageToken = () => localStorage.removeItem('token');
export const getLocalStorageShiftTab = () => localStorage.getItem('current_shift_tab');
export const setLocalStorageShiftTab = (tab) => localStorage.setItem('current_shift_tab', tab);
export const removeLocalStorageShiftTab = () => localStorage.removeItem('current_shift_tab');
export const getLocalStorageEducatorTab = () => localStorage.getItem('current_educator_tab');
export const setLocalStorageEducatorTab = (tab) => localStorage.setItem('current_educator_tab', tab);
export const removeLocalStorageEducatorTab = () => localStorage.removeItem('current_educator_tab');

const navigateToLogin = () => {
  window.location.href = '/login';
}
const navigateToHome = () => {
  window.location.href = '/';
}

// Role cache populated by useUser. Stays null until login resolves; treat
// null as "not yet known" rather than "no access".
//
// Contract: callers MUST pass an explicit role string ('owner', 'manager',
// …) for a logged-in user, or `null` for "no role / not yet known". Passing
// `undefined` (or omitting) collapses to `null` via `?? null` — this is the
// "unknown" signal, NOT a way to say "user has no privileges". A user who
// has explicitly been verified as having no privileged role should be
// represented with a sentinel like 'educator' or 'co_only', so the
// downstream `== null` short-circuit in assertManageAdminAllowed doesn't
// silently let privileged calls through.
//
// As of writing the only caller is `useUser.js`, which passes
// `userOrg?.access ?? null`. `userOrg.access` is always a concrete role
// string for any logged-in user with an org (including educator and
// co_only); the `?? null` only fires for users with no organisations at
// all, which is the "unknown" state. No caller passes `null` as a
// verified-no-access marker, so the `== null` relaxation is safe.
let cachedUserRole = null;
let cachedIsAdmin = false;
export const setApiUserRole = ({ role, isAdmin }) => {
  // Dev-mode tripwire for the contract above: callers that pass `undefined`
  // for `role` are almost always a bug (forgot to thread the role through,
  // or hit a destructure default). We coerce to null for safety, but the
  // warn surfaces it before it ships.
  if (process.env.NODE_ENV !== 'production' && role === undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      '[setApiUserRole] called with role=undefined. Pass null explicitly ' +
      'for "unknown", or a role string for a known user. Coercing to null.',
    );
  }
  cachedUserRole = role ?? null;
  cachedIsAdmin = !!isAdmin;
};

const isPrivilegedEndpoint = (endpoint) =>
  typeof endpoint === 'string' &&
  (endpoint.startsWith('/manage') || endpoint.startsWith('/admin'));

// Roles permitted to hit /manage and /admin endpoints. Educators (and
// users with no org role) are blocked here so the network call is never
// made and the 403-driven reload loop in handleResponse can't trigger.
// Derived from PROVIDER_READ_ROLES so any role list change is single-file.
//
// A `null` cachedUserRole means the role hasn't loaded yet (useUser's effect
// runs after children's effects, so the first privileged query from a child
// page can race ahead of setApiUserRole). Treat null as "unknown, let the
// server decide" rather than "denied" — the server still gates with
// hasOrgAccess, so unauthorized callers get a real 403 either way.
const PROVIDER_ROLES_SET = new Set(PROVIDER_READ_ROLES);
const assertManageAdminAllowed = (endpoint) => {
  if (!isPrivilegedEndpoint(endpoint)) return;
  if (cachedIsAdmin) return;
  // History: the original check used `=== null` to cover the "role not
  // yet loaded" case. That guard was a tiny bit too tight — `undefined`
  // can reach this path two ways: (a) via a destructure default if a
  // caller forgets to thread `role` through `setApiUserRole`, or (b) if
  // a privileged endpoint is hit before `setApiUserRole` has ever been
  // called (module-init phase, lazy-loaded chunk that mounts before the
  // root layout hydrates). Both are "role not yet known". The
  // `setApiUserRole` dev-mode warn catches (a); (b) is silent in dev
  // because the function never ran, which is intentional — the server's
  // `hasOrgAccess` is the authoritative gate either way.
  if (cachedUserRole == null) return; // intentional loose: matches null AND undefined — see block comment above
  if (PROVIDER_ROLES_SET.has(cachedUserRole)) return;
  const error = new Error('You do not have permission to perform this action.');
  error.status = 403;
  throw error;
};
const getStandardHeaders = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    'Content-Type': 'application/json',
    'Time-Zone': timezone,
  };
};
function convertObjectToURLParameters(params) {
  return '?' + Object.keys(params).map((key) => {
    return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
  }).join('&');
}
// `signal` (optional): an AbortSignal, e.g. React Query's, so a request nobody
// needs any more (a superseded search) is cancelled, and the API stops its
// database work for it (QC-2978).
export const get = async (endpoint, headers = {}, parameter, { signal } = {}) => {
  assertManageAdminAllowed(endpoint);
  let url = `${BASE_URL}${endpoint}`
  if (parameter){
    const paramsString = convertObjectToURLParameters(parameter);
    url = `${url}${paramsString}`
  }
  const response = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { ...getStandardHeaders(), ...headers },
    signal,
  });
  return handleResponse(response, endpoint);
};

// Fetches a file endpoint and returns the raw Blob. Returns null for the
// known "no file" sentinels. Throws on non-2xx so callers can surface a real
// error rather than wrapping an error body in a valid blob: URL (which would
// render as a broken image). Prefer this when you want to own the object-URL
// lifecycle yourself (e.g. cache the Blob and mint/revoke per consumer).
export const getFileAsBlobObject = async (endpoint, headers = {}, parameter) => {
  if (endpoint.endsWith('undefined') || endpoint.endsWith('null')) {
    return null;
  }
  assertManageAdminAllowed(endpoint);
  let url = `${BASE_URL}${endpoint}`
  if (parameter){
    const paramsString = convertObjectToURLParameters(parameter);
    url = `${url}${paramsString}`
  }
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { ...getStandardHeaders(), ...headers },
    credentials: 'include',
  });
  if (!response.ok) {
    const error = new Error(`Failed to fetch file (${response.status})`);
    error.status = response.status;
    throw markRetriesExhausted(error, response);
  }
  return response.blob();
};

// Convenience wrapper that returns an object URL string for the fetched Blob.
// Used by the various one-shot download call sites. NOTE: the returned URL is
// owned by the caller and should be revoked once consumed.
export const getFileAsBlob = async (endpoint, headers = {}, parameter) => {
  const blob = await getFileAsBlobObject(endpoint, headers, parameter);
  if (!blob) return null;
  return URL.createObjectURL(blob);
};

export const post = async (endpoint, body, headers = {}) => {
  assertManageAdminAllowed(endpoint);
  const response = await apiFetch(`${BASE_URL}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...getStandardHeaders(), ...headers },
    body: JSON.stringify(body),
  });
  return handleResponse(response, endpoint);
};
export const postFormData = async (endpoint, body, headers = {}) => {
  assertManageAdminAllowed(endpoint);
  let response;
  try {
    response = await axios.postForm(`${BASE_URL}${endpoint}`, body, {
      headers: { ...getStandardHeaders(), ...headers },
      withCredentials: true,
    });
  } catch (error) {
    // axios rejects non-2xx itself; classify before rethrowing.
    if (isTransportFailure(error)) reportServiceFailure('network');
    else if (isServiceFailure(error.response)) reportServiceFailure('gateway');
    throw error;
  }
  return handleResponse(response, endpoint);
}
export const postFile = async (endpoint, file, headers = {}) => {
  assertManageAdminAllowed(endpoint);
  const reader = new FileReader();
  const promise = new Promise((resolve, reject) => {
    reader.onloadend = async () => {
      // Settle on every path: a throw in here used to leave the caller's
      // promise pending forever.
      try {
        const base64Data = reader.result.split(',')[1];
        const response = await apiFetch(`${BASE_URL}${endpoint}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getStandardHeaders(), ...headers },
          body: JSON.stringify({ base64Data, contentType: file.type }),
        }, { retry: false });
        resolve(await handleResponse(response, endpoint));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
  });
  reader.readAsDataURL(file);
  return promise;
};

export const put = async (endpoint, body, headers = {}) => {
  assertManageAdminAllowed(endpoint);
  const response = await apiFetch(`${BASE_URL}${endpoint}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { ...getStandardHeaders(), ...headers },
    body: JSON.stringify(body),
  });
  return handleResponse(response, endpoint);
};

export const patch = async (endpoint, body = {}, headers = {}) => {
  assertManageAdminAllowed(endpoint);
  const response = await apiFetch(`${BASE_URL}${endpoint}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { ...getStandardHeaders(), ...headers },
    body: Object.keys(body).length ? JSON.stringify(body) : undefined,
  });
  return handleResponse(response, endpoint);
};

export const del = async (endpoint, parameter, headers = {}) => {
  assertManageAdminAllowed(endpoint);
  let url = `${BASE_URL}${endpoint}`
  if (parameter){
    const paramsString = convertObjectToURLParameters(parameter);
    url = `${url}${paramsString}`
  }
  const response = await apiFetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: { ...getStandardHeaders(), ...headers },
  });
  return handleResponse(response, endpoint);
};

export const migrateTokenToCookie = async (token) => {
  try {
    if (!token) {
      return { success: false, message: 'No token found in localStorage' };
    }

    const response = await apiFetch(`${BASE_URL}/migrate-token`, {
      method: 'POST',
      headers: { ...getStandardHeaders() },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      return { success: false, message: 'Token migration failed' };
    }

    const data = await response.json();

    // Remove token from localStorage after successful migration
    removeLocalStorageToken();

    return { success: true, data };
  } catch (error) {
    console.error('Token migration error:', error);
    return { success: false, message: error.message };
  }
};

const readResponse = async (response, endpoint) => {
  if(response.data) {
    return response.data;
  }
  // Read once; JSON only when it is JSON (apiResponse.js).
  const body = await readResponseBody(response);
  const { data, isJson } = body;
  if (response.status === 403) {
    // /manage and /admin 403s are expected for non-privileged roles; throwing
    // is enough. Redirecting home here causes a reload loop when an educator's
    // landing page incidentally triggers a privileged call.
    if (!isPrivilegedEndpoint(endpoint)) {
      navigateToHome();
    }
    const error = new Error('You do not have permission to perform this action.');
    error.status = 403;
    error.data = isJson ? data : null;
    throw error;
  }
  if (!response.ok && (response.status !== 401 && response.status !== 403)) {
    throw responseError(response, body);
  }
  const currentRoute = window.location.pathname;
  if (response.status === 401 && currentRoute !== '/login') {
    setCurrentRouteBeforeLogin(currentRoute);
    navigateToLogin();
    throw new Error('Session expired. Please log in again.');
  }
  if (response.status === 401) {
    throw new Error('Authentication failed');
  }

  return data; // Return the parsed data
};

const handleResponse = async (response, endpoint) => {
  try {
    return await readResponse(response, endpoint);
  } catch (error) {
    throw markRetriesExhausted(error, response);
  }
};
